/**
 * PAN Entity Type Validation Utility
 *
 * The 4th character of a PAN number identifies the entity type:
 *   P = Individual / Proprietorship
 *   F = Partnership Firm
 *   C = Company (Private Limited, Public Limited, LLP)
 *   T = Trust / Society (AOP/BOI)
 *   H = Hindu Undivided Family (HUF)
 *   A = Association of Persons (AOP)
 *   B = Body of Individuals (BOI)
 *   G = Government
 *   J = Artificial Juridical Person
 *   L = Local Authority
 *
 * PAN format: AAAAA9999A
 *   - Chars 1-3: AAA (first 3 letters of surname/entity name)
 *   - Char  4:   Entity type code
 *   - Char  5:   First letter of name
 *   - Chars 6-9: 4 digits
 *   - Char  10:  Check letter
 */

/** PAN format regex — must be fully valid before entity-type check runs */
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

/**
 * Map from PAN 4th-character code → allowed entity type form values (all lowercase)
 */
const PAN_CHAR_TO_ENTITY_TYPES = {
  P: ["proprietorship", "society"], // Individual / Proprietor
  F: ["partnership", "llp"], // Firm (Partnership / LLP)
  C: ["private_limited", "public_limited", "llp"], // Company
  T: ["trust"], // Trust / AOP / BOI
  H: ["proprietorship"], // HUF — treated as proprietorship
  A: ["society"], // AOP
  B: ["society"], // BOI
};

/**
 * Human-readable label for each PAN entity code
 */
const PAN_CHAR_LABEL = {
  P: "Individual / Proprietorship",
  F: "Partnership Firm",
  C: "Company",
  T: "Trust",
  H: "Hindu Undivided Family (HUF)",
  A: "Association of Persons (AOP)",
  B: "Body of Individuals (BOI)",
  G: "Government",
  J: "Artificial Juridical Person",
  L: "Local Authority",
};

/**
 * Extract the entity-type character (4th character) from a PAN number.
 * @param {string} pan - PAN number (10 chars)
 * @returns {string} - Single uppercase character, or "" if invalid
 */
export const getPANEntityChar = (pan) => {
  if (!pan || typeof pan !== "string") return "";
  const cleaned = pan.trim().toUpperCase();
  if (cleaned.length < 4) return "";
  return cleaned[3]; // 0-indexed → 4th character
};

/**
 * Validate that the selected entity type matches the PAN entity type character.
 * Only runs when:
 *  - PAN is fully valid (10 chars, correct format)
 *  - entityType is selected
 *  - We have a known mapping for the PAN's 4th character
 *
 * @param {string} pan         - PAN number (e.g. "AABCP1234D")
 * @param {string} entityType  - Form entity type value (e.g. "proprietorship")
 * @returns {{ isValid: boolean, message: string, panEntityChar: string, panEntityLabel: string }}
 */
export const validatePANEntityType = (pan, entityType) => {
  const pass = {
    isValid: true,
    message: "",
    panEntityChar: "",
    panEntityLabel: "",
  };

  if (!pan || !entityType) return pass;

  const cleaned = pan.trim().toUpperCase();

  // Only validate when PAN is fully valid — skip if still being typed
  if (!PAN_REGEX.test(cleaned)) return pass;

  const panChar = cleaned[3];
  const allowedTypes = PAN_CHAR_TO_ENTITY_TYPES[panChar];
  const panEntityLabel = PAN_CHAR_LABEL[panChar] || `type "${panChar}"`;

  // No mapping for this char — skip the check (don't block unknown types)
  if (!allowedTypes) return pass;

  const isValid = allowedTypes.includes(entityType.trim().toLowerCase());

  if (!isValid) {
    return {
      isValid: false,
      message: `PAN indicates ${panEntityLabel} — does not match selected entity type`,
      panEntityChar: panChar,
      panEntityLabel,
    };
  }

  return { isValid: true, message: "", panEntityChar: panChar, panEntityLabel };
};

/**
 * Get the expected entity type label from a PAN number (for display).
 * @param {string} pan - PAN number
 * @returns {string} - Human-readable entity type label
 */
export const getExpectedEntityTypeFromPAN = (pan) => {
  const panChar = getPANEntityChar(pan);
  return PAN_CHAR_LABEL[panChar] || "";
};
